import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { SiChessdotcom } from "react-icons/si";
import { FaChessKnight, FaChessBoard, FaExternalLinkAlt } from "react-icons/fa";
import axios from 'axios';

const USERNAME = 'joshrubow';
const RAPID_TIME_CONTROL = '600';

const getResultColor = (result) => {
    if (result === 'win') return '#15803d';
    if (result === 'checkmated' || result === 'resigned' || result === 'timeout') return '#fa4444';
    return '#888';
};

/* ─── Rapid Rating Graph + Latest Games ─── */
const RatingGraph = () => {
    const [data, setData] = useState([]);
    const [latestGames, setLatestGames] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchThreeMonthsData = async () => {
            try {
                setLoading(true);
                const now = new Date();
                let month = now.getMonth() + 1;
                let year = String(now.getFullYear());

                const months = [
                    { y: year, m: String(month).padStart(2, '0') },
                    { y: year, m: String(month - 1).padStart(2, '0') },
                    { y: year, m: String(month - 2).padStart(2, '0') }
                ];

                const requests = months.map(date =>
                    axios.get(`https://api.chess.com/pub/player/${USERNAME}/games/${date.y}/${date.m}`)
                );

                const responses = await Promise.all(requests);
                const allGames = responses.flatMap(r => r.data.games);
                allGames.sort((a, b) => a.end_time - b.end_time);

                const chartData = allGames.map(game => {
                    if (game.time_control === RAPID_TIME_CONTROL) {
                        const isWhite = game.white.username.toLowerCase() === USERNAME.toLowerCase();
                        const stats = isWhite ? game.white : game.black;
                        return {
                            time: new Date(game.end_time * 1000).toLocaleDateString(),
                            rating: stats.rating,
                            opponent: isWhite ? game.black.username : game.white.username,
                            result: stats.result
                        };
                    }
                    return null;
                }).filter(Boolean);

                setData(chartData);
                setLatestGames([...allGames].reverse().slice(0, 4));
                setLoading(false);
            } catch (error) {
                console.error('Error fetching Chess data:', error);
                setLoading(false);
            }
        };

        fetchThreeMonthsData();
    }, []);

    if (loading) return <div className="chess-loading">Loading Chess Stats...</div>;

    return (
        <div className="chess-graph-container">
            <div className="chess-graph-panel">
                <div className="chessdisplay-title">
                    <a href="https://www.chess.com/member/joshrubow" target="_blank" rel="noreferrer" className="nav-icon"><SiChessdotcom /></a>
                    <h3>Rapid Rating Progress (3 months)</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                        <XAxis dataKey="time" stroke="#52525b" tick={{ fill: '#a3a3a3', fontSize: 12 }} minTickGap={30} />
                        <YAxis stroke="#52525b" tick={{ fill: '#a3a3a3', fontSize: 12 }} domain={['dataMin - 50', 'dataMax + 50']} />
                        <Tooltip
                            contentStyle={{ backgroundColor: '#111111', border: '1px solid #262626', color: '#f5f5f5' }}
                            labelStyle={{ color: '#a3a3a3' }}
                            itemStyle={{ color: '#ffffff' }}
                        />
                        <Legend wrapperStyle={{ color: '#a3a3a3' }} />
                        <Line name="ELO Rating" type="monotone" dataKey="rating" stroke="#e5e5e5" strokeWidth={2.5} dot={false} activeDot={{ r: 6, fill: '#ffffff' }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="chessdisplay-latest">
                <h3 className="chess-panel-title">Latest Games</h3>
                {latestGames.map((game, index) => {
                    const isWhite = game.white.username.toLowerCase() === USERNAME.toLowerCase();
                    const opponent = isWhite ? game.black : game.white;
                    const result = isWhite ? game.white.result : game.black.result;
                    const color = getResultColor(result);
                    return (
                        <div key={index} className="chess-game-row" style={{ borderLeft: `4px solid ${color}` }}>
                            <div>
                                <strong>vs {opponent.username}</strong>
                                <div className="chess-game-sub">{opponent.rating} Elo</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <span style={{ color, fontWeight: 'bold' }}>{result.toUpperCase()}</span>
                                <div className="chess-game-sub">{new Date(game.end_time * 1000).toLocaleDateString()}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ─── Player Stats (wins / draws / losses) ─── */
const ChessStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`https://api.chess.com/pub/player/${USERNAME}/stats`)
            .then(res => { setStats(res.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return null;
    if (!stats) return null;

    const rapid = stats.chess_rapid?.record;
    const blitz = stats.chess_blitz?.record;
    const bullet = stats.chess_bullet?.record;

    const StatBox = ({ label, record, rating }) => {
        if (!record) return null;
        const total = record.win + record.draw + record.loss;
        const winPct = total ? Math.round((record.win / total) * 100) : 0;
        return (
            <div className="chess-stat-box">
                <div className="chess-stat-label">{label}</div>
                <div className="chess-stat-rating">{rating?.last?.rating ?? '—'}</div>
                <div className="chess-stat-sub">Current Rating</div>
                <div className="chess-stat-record">
                    <span className="stat-win">{record.win}W</span>
                    <span className="stat-draw">{record.draw}D</span>
                    <span className="stat-loss">{record.loss}L</span>
                </div>
                <div className="chess-stat-pct">{winPct}% win rate</div>
            </div>
        );
    };

    return (
        <div className="chess-stats-row">
            <div className="chess-stats-header">
                <FaChessKnight className="chess-stats-icon" />
                <h3>Player Statistics</h3>
            </div>
            <div className="chess-stats-boxes">
                <StatBox label="Rapid" record={rapid} rating={stats.chess_rapid} />
                <StatBox label="Blitz" record={blitz} rating={stats.chess_blitz} />
                <StatBox label="Bullet" record={bullet} rating={stats.chess_bullet} />
            </div>
        </div>
    );
};

/* ─── Puzzle of the Day ─── */
const PuzzleOfTheDay = () => {
    const [puzzle, setPuzzle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get('https://api.chess.com/pub/puzzle')
            .then(res => { setPuzzle(res.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return <div className="chess-loading">Loading puzzle...</div>;
    if (!puzzle) return null;

    return (
        <div className="chess-puzzle-container">
            <div className="chess-puzzle-header">
                <FaChessBoard className="chess-stats-icon" />
                <h3>Puzzle of the Day</h3>
            </div>
            <div className="chess-puzzle-body">
                {puzzle.image && (
                    <div className="chess-puzzle-img-wrap">
                        <img src={puzzle.image} alt="Chess puzzle" className="chess-puzzle-img" />
                    </div>
                )}
                <div className="chess-puzzle-info">
                    <p className="chess-puzzle-title">{puzzle.title}</p>
                    <p className="chess-puzzle-rating">Rating: <strong>{puzzle.rating}</strong></p>
                    <a href={puzzle.url} target="_blank" rel="noreferrer" className="chess-puzzle-link">
                        Solve on Chess.com <FaExternalLinkAlt style={{ fontSize: '12px' }} />
                    </a>
                </div>
            </div>
        </div>
    );
};

/* ─── Main Export ─── */
const ChessDashboard = () => {
    return (
        <div className="chess-dashboard">
            <ChessStats />
            <RatingGraph />
            {/* <PuzzleOfTheDay /> */}
        </div>
    );
};

export default ChessDashboard;
